/* License activation form — posts to the Clean Slate licensing backend.
 *
 * Flow:
 *   1. Client-side validate: license key shape (CS-XXXX-XXXX-XXXX-XXXX) +
 *      shop domain ends in .myshopify.com.
 *   2. POST { license_key, shop_domain } to {api}/activate.
 *   3. Render status from response. Translate machine errors into
 *      friendly messages. On success, optionally redirect.
 *
 * Lives at the storefront origin (e.g. cleanslate.shop), the backend
 * is a different origin — CORS is configured permissively on the worker.
 */
(function () {
  'use strict';

  var KEY_RE = /^CS-[0-9A-HJ-NP-TV-Z]{4}-[0-9A-HJ-NP-TV-Z]{4}-[0-9A-HJ-NP-TV-Z]{4}-[0-9A-HJ-NP-TV-Z]{4}$/;
  var SHOP_RE = /^[a-z0-9][a-z0-9-]{0,58}[a-z0-9]\.myshopify\.com$/;

  function normalizeLicenseKey(raw) {
    var v = (raw || '').trim().toUpperCase().replace(/[\s_-]/g, '');
    if (v.length !== 18 || v.slice(0, 2) !== 'CS') return (raw || '').trim().toUpperCase();
    return v.slice(0, 2) + '-' + v.slice(2, 6) + '-' + v.slice(6, 10) + '-' + v.slice(10, 14) + '-' + v.slice(14, 18);
  }

  function normalizeShopDomain(raw) {
    var v = (raw || '').trim().toLowerCase();
    v = v.replace(/^https?:\/\//, '');
    v = v.replace(/\/.*$/, '');
    return v;
  }

  function setStatus(el, kind, text) {
    el.className = 'csp-activate__status' + (kind ? ' is-' + kind : '');
    el.textContent = text || '';
  }

  function setLoading(form, isLoading) {
    var btn = form.querySelector('.csp-activate__submit');
    if (isLoading) {
      btn.disabled = true;
      btn.classList.add('is-loading');
    } else {
      btn.disabled = false;
      btn.classList.remove('is-loading');
    }
  }

  function friendlyError(payload, support) {
    if (!payload || typeof payload !== 'object') {
      return 'Something went wrong. Try again in a moment, or email ' + support + '.';
    }
    if (payload.message) return payload.message;
    switch (payload.error) {
      case 'not_found':         return "We can't find that license key. Double-check it — keys look like CS-XXXX-XXXX-XXXX-XXXX.";
      case 'revoked':           return 'This license has been revoked. Contact ' + support + ' for help.';
      case 'wrong_domain':      return 'This license is already activated on a different Shopify store. Email ' + support + ' to transfer it.';
      case 'invalid_license_key': return 'That license key looks invalid. Check for typos and try again.';
      case 'invalid_shop_domain': return 'Please enter your store URL exactly as it appears in your Shopify admin (e.g. my-shop.myshopify.com).';
      case 'rate_limited':      return 'Too many attempts. Try again in an hour.';
      default:                  return 'Something went wrong (' + (payload.error || 'unknown') + '). Email ' + support + '.';
    }
  }

  function attach(form) {
    var api = (form.dataset.api || '').replace(/\/+$/, '');
    var support = form.dataset.support || 'support@cleanslate.shop';
    var successUrl = form.dataset.successUrl || '';
    var statusEl = form.querySelector('.csp-activate__status');
    var keyInput = form.querySelector('input[name="license_key"]');
    var shopInput = form.querySelector('input[name="shop_domain"]');

    /* Auto-normalize key as the user types so they SEE the canonical format. */
    keyInput.addEventListener('blur', function () {
      keyInput.value = normalizeLicenseKey(keyInput.value);
    });
    shopInput.addEventListener('blur', function () {
      shopInput.value = normalizeShopDomain(shopInput.value);
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      setStatus(statusEl, null, '');

      var key = normalizeLicenseKey(keyInput.value);
      var shop = normalizeShopDomain(shopInput.value);
      keyInput.value = key;
      shopInput.value = shop;

      if (!KEY_RE.test(key)) {
        setStatus(statusEl, 'error', 'License key should look like CS-XXXX-XXXX-XXXX-XXXX. Check for typos.');
        keyInput.focus();
        return;
      }
      if (!SHOP_RE.test(shop)) {
        setStatus(statusEl, 'error', 'Enter your store URL like my-shop.myshopify.com.');
        shopInput.focus();
        return;
      }
      if (!api) {
        setStatus(statusEl, 'error', 'This page is misconfigured — no backend URL set. Email ' + support + '.');
        return;
      }

      setLoading(form, true);

      fetch(api + '/activate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ license_key: key, shop_domain: shop }),
        credentials: 'omit',
        cache: 'no-store'
      })
      .then(function (res) {
        return res.json().then(function (body) { return { status: res.status, body: body }; });
      })
      .then(function (r) {
        setLoading(form, false);
        if (r.status >= 200 && r.status < 300 && r.body && r.body.ok) {
          var msg = r.body.status === 'already_active'
            ? 'This license is already active on ' + shop + '. You’re all set.'
            : 'Activated! Your license is now bound to ' + shop + '.';
          setStatus(statusEl, 'success', msg);
          if (successUrl) {
            setTimeout(function () { window.location.href = successUrl; }, 1500);
          }
          return;
        }
        setStatus(statusEl, 'error', friendlyError(r.body, support));
      })
      .catch(function () {
        setLoading(form, false);
        setStatus(statusEl, 'error', 'Network error. Check your connection and try again.');
      });
    });
  }

  function init() {
    document.querySelectorAll('.csp-activate__form').forEach(attach);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();